import { useEffect, useState } from "react";
import { Plus, Trash2, ShieldCheck, User, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/StatCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RolesManager } from "@/components/RolesManager";

const empty = { name: "", email: "", password: "", role: "staff" };

export default function Users() {
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [reset, setReset] = useState(null); // {user, password}
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const isOwner = me.role === "owner";
  const assignable = roles.filter((r) => r.key !== "owner" && (isOwner || r.permissions.every((p) => me.permissions?.includes(p))));
  const roleName = (k) => roles.find((r) => r.key === k)?.name || k;

  const load = () => { api.get("/users").then((r) => setList(r.data)); api.get("/roles").then((r) => setRoles(r.data)); };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post("/users", form); toast.success("Pengguna ditambahkan"); setForm(null); load(); } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const patch = async (u, body, msg) => {
    try { await api.put(`/users/${u.id}`, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.put(`/users/${reset.user.id}`, { password: reset.password }); toast.success(`Password ${reset.user.name} berhasil direset`); setReset(null); } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus pengguna ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Pengguna dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Kelola Pengguna" subtitle="Tambah akun, atur role, dan tentukan hak akses setiap role">
        <Button className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setForm(empty)} data-testid="btn-add-user"><Plus className="h-4 w-4 mr-1" /> Tambah Pengguna</Button>
      </PageHeader>

      <div className="card-aqua overflow-hidden rise rise-1">
        <table className="w-full text-sm" data-testid="table-users">
          <thead className="table-head"><tr><th className="text-left px-4 py-3">Nama</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Dibuat</th><th className="px-4 py-3 text-right">Aksi</th></tr></thead>
          <tbody>
            {list.map((u, idx) => (
              <tr key={u.id} className="border-t border-sky-50 hover:bg-sky-50/50" data-testid={`user-row-${idx}`}>
                <td className="px-4 py-3 font-semibold text-slate-800 flex items-center gap-2">
                  <span className={`h-8 w-8 rounded-full flex items-center justify-center ${u.role === "owner" ? "bg-amber-500 text-white" : u.role === "admin" ? "bg-slate-900 text-sky-300" : "bg-sky-100 text-sky-700"}`}>{u.role !== "staff" ? <ShieldCheck className="h-4 w-4" /> : <User className="h-4 w-4" />}</span>
                  {u.name}{u.id === me.id && <span className="text-xs text-slate-400">(Anda)</span>}
                  {u.auth_provider === "google" && <span className="text-[10px] font-semibold text-slate-500 border border-slate-200 rounded px-1" data-testid={`badge-google-${idx}`}>Google</span>}
                  {u.approval_pending && <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`badge-pending-user-${idx}`}>Menunggu persetujuan</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  {u.role === "owner" ? <span className="inline-flex text-xs font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`badge-owner-${idx}`}>Pemilik (akun utama)</span> :
                  <Select value={u.role} onValueChange={(v) => patch(u, { role: v }, "Role diperbarui")} disabled={u.id === me.id || !isOwner}>
                    <SelectTrigger className="h-8 w-40" data-testid={`select-user-role-${idx}`}><SelectValue placeholder="Pilih role">{roleName(u.role)}</SelectValue></SelectTrigger>
                    <SelectContent>{roles.filter((r) => r.key !== "owner").map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={u.is_active} disabled={u.id === me.id || (u.role === "owner" && me.role !== "owner")} onCheckedChange={(v) => patch(u, { is_active: v }, v ? "Pengguna diaktifkan" : "Pengguna dinonaktifkan")} data-testid={`switch-user-active-${idx}`} />
                    <span className={`text-xs font-semibold ${u.is_active ? "text-emerald-600" : "text-slate-400"}`}>{u.is_active ? "Aktif" : "Nonaktif"}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{u.created_at ? fmtDate(u.created_at.slice(0, 10)) : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    {u.role !== "owner" || me.role === "owner" ? <Button variant="ghost" size="sm" className="h-8 text-sky-700" onClick={() => setReset({ user: u, password: "" })} data-testid={`btn-reset-password-${idx}`}><KeyRound className="h-4 w-4 mr-1" /> Reset Password</Button> : <span className="text-xs text-slate-400">Hanya akun utama</span>}
                    {u.id !== me.id && u.role !== "owner" && <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => remove(u)} data-testid={`btn-delete-user-${idx}`}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RolesManager isOwner={isOwner} roles={roles} onChanged={load} />

      <Dialog open={!!reset} onOpenChange={(o) => !o && setReset(null)}>
        <DialogContent className="sm:max-w-sm rounded-2xl" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle>Reset Password Pengguna</DialogTitle></DialogHeader>
          {reset && (
            <form onSubmit={submitReset} className="space-y-4">
              <p className="text-sm text-slate-600">Atur password baru untuk <b>{reset.user.name}</b> ({reset.user.email}). Sampaikan password ini secara langsung kepada pengguna.</p>
              <div className="space-y-1.5"><Label>Password Baru</Label><Input type="text" value={reset.password} onChange={(e) => setReset({ ...reset, password: e.target.value })} required minLength={6} placeholder="Minimal 6 karakter" data-testid="input-admin-reset-password" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setReset(null)}>Batal</Button>
                <Button type="submit" disabled={saving || reset.password.length < 6} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-admin-reset-submit">{saving ? "Menyimpan..." : "Simpan Password"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="user-form-dialog">
          <DialogHeader><DialogTitle>Tambah Pengguna</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5"><Label>Nama</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="input-user-name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="input-user-email" /></div>
              <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} data-testid="input-user-password" /></div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger data-testid="select-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{assignable.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForm(null)}>Batal</Button>
                <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-user-submit">{saving ? "Menyimpan..." : "Simpan"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
